import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Spin } from 'antd';
import { createSafeS3Url } from 'shared/utils';
import { Document, Page } from 'react-pdf';

// Configure PDF.js worker for react-pdf 10.x
import { pdfjs } from 'react-pdf';
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

/**
 * Transform URL and determine previewer type
 * For existing URLs: Convert to optimized PDF if possible
 * For file objects: Create local URL and determine type
 */
const transformUrl = (input) => {
  // If input is a File object, create local URL and determine type
  if (input instanceof File) {
    const localUrl = URL.createObjectURL(input);

    // Determine previewer type based on file type
    const isImage = input.type.startsWith('image/');
    const previewerType = isImage ? 'image' : 'pdf';

    return {
      url: localUrl,
      previewerType,
    };
  }

  // If input is a string URL, try to convert to optimized PDF
  if (typeof input === 'string') {
    // Handle blob URLs - these are local file objects, treat as images
    if (input.startsWith('blob:')) {
      return {
        url: input,
        previewerType: 'image',
      };
    }

    try {
      // Use the createSafeS3Url utility to properly encode the URL
      const cleanUrl = createSafeS3Url(input);

      const urlParts = cleanUrl.split('/');
      const originalFileName = urlParts[urlParts.length - 1];

      const baseFileName = originalFileName.split('.')[0];
      const optimizedFileName = `${baseFileName}_optimized.pdf`;
      const baseUrl = cleanUrl.substring(0, cleanUrl.lastIndexOf('/'));
      const urlObj = new URL(baseUrl);
      const bucketAndRegion = `${urlObj.hostname}`;
      let optimizedPdfUrl = `https://${bucketAndRegion}/`;

      if (cleanUrl.includes('UFP-Workorder/production/')) {
        optimizedPdfUrl += 'opt-pdf/';
      } else if (cleanUrl.includes('/staging/')) {
        optimizedPdfUrl += 'opt-pdf/staging/';
      } else {
        const fileExtension = originalFileName.split('.').pop()?.toLowerCase();
        let previewerType = 'pdf';

        if (fileExtension) {
          if (['jpg', 'jpeg', 'png'].includes(fileExtension)) {
            previewerType = 'image';
          }
        }

        return {
          url: cleanUrl,
          previewerType: previewerType,
        };
      }

      optimizedPdfUrl += optimizedFileName;

      return {
        url: optimizedPdfUrl,
        previewerType: 'pdf',
      };
    } catch (error) {
      console.error('❌ Error transforming URL:', error);
      Sentry.captureException(error, {
        tags: {
          component: 'ThumbnailViewer',
          method: 'transformUrl',
          url: input,
        },
        extra: {
          url: input,
        },
      });
      return {
        url: input, // Fallback to original URL
        previewerType: 'pdf',
      };
    }
  }

  // Fallback
  console.log('⚠️ Unknown input type, returning fallback');
  return {
    url: String(input),
    previewerType: 'pdf',
  };
};

/**
 * Convert data to proper format for react-doc-viewer
 */
const prepareDocumentData = (media) => {
  if (
    media instanceof File ||
    media.startsWith('http://') ||
    media.startsWith('https://') ||
    media.startsWith('blob:')
  ) {
    const fileName =
      media instanceof File ? media.name : media.split('/').pop() || 'document';

    // Transform URL and get previewer type
    const { url, previewerType } = transformUrl(media);

    const documentData = [
      {
        uri: url,
        fileName:
          previewerType === 'pdf'
            ? fileName.replace(/\.[^/.]+$/, '.pdf')
            : fileName,
        fileType: previewerType === 'pdf' ? 'application/pdf' : 'image/*',
      },
    ];

    return { documentData, previewerType };
  }

  return { documentData: [], previewerType: 'pdf' };
};

const ThumbnailViewer = ({ media, width = 200, height = 150, onClick }) => {
  const [documentData, setDocumentData] = useState([]);
  const [previewerType, setPreviewerType] = useState('pdf');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Remove redundant worker configuration - already set at module level
    if (!media) {
      setLoading(false);
      return;
    }

    const loadDocument = async () => {
      try {
        setLoading(true);
        setError(null);

        // Prepare document data - transform URL and get previewer type
        const { documentData: docs, previewerType: type } =
          prepareDocumentData(media);

        // Check if the blob URL is still valid
        if (docs[0]?.uri && docs[0].uri.startsWith('blob:')) {
          try {
            // Test if the blob URL is still accessible
            const response = await fetch(docs[0].uri);
            if (!response.ok) {
              throw new Error('Blob URL is no longer valid');
            }
          } catch (blobError) {
            console.warn('⚠️ Blob URL is invalid, recreating...', blobError);
            // If blob URL is invalid, recreate it for File objects
            if (media instanceof File) {
              const newBlobUrl = URL.createObjectURL(media);
              docs[0].uri = newBlobUrl;
            }
          }
        }

        setDocumentData(docs);
        setPreviewerType(type);
        setLoading(false);
      } catch (err) {
        console.error('❌ Error preparing thumbnail viewer:', err);
        setError('Failed to load thumbnail');
        setLoading(false);
        Sentry.captureException(err, {
          tags: {
            component: 'ThumbnailViewer',
            method: 'loadDocument',
          },
          extra: {
            url: media,
          },
        });
      }
    };

    loadDocument();
  }, [media]);

  // Container style for thumbnail
  const containerStyle = {
    width: width,
    height: height,
    border: '1px solid #d9d9d9',
    borderRadius: '4px',
    overflow: 'hidden',
    cursor: 'pointer',
    backgroundColor: '#fafafa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <Spin size="small" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div
          style={{
            fontSize: '12px',
            color: '#999',
            textAlign: 'center',
            padding: '8px',
          }}
        >
          Failed to load
        </div>
      </div>
    );
  }

  if (documentData.length === 0) {
    return (
      <div style={containerStyle}>
        <div
          style={{
            fontSize: '12px',
            color: '#999',
            textAlign: 'center',
            padding: '8px',
          }}
        >
          No preview
        </div>
      </div>
    );
  }

  // Thumbnail viewer with dual previewer support
  return (
    <div
      style={containerStyle}
      onClick={onClick}
      title="Click to view full document"
    >
      {previewerType === 'image' ? (
        // Image thumbnail viewer
        <img
          src={documentData[0]?.uri}
          alt="Document Thumbnail"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '4px',
          }}
          onLoad={() => {}}
          onError={(e) => {
            console.error('❌ ThumbnailViewer image failed to load:', e);
            console.error(
              '❌ ThumbnailViewer image src was:',
              documentData[0]?.uri
            );
            setError('Failed to load image thumbnail');
          }}
        />
      ) : (
        // PDF thumbnail viewer
        <div style={{ transform: 'scale(0.8)', transformOrigin: 'center' }}>
          <Document
            file={documentData[0]?.uri}
            loading={<Spin size="small" />}
            error={<div>Error loading PDF</div>}
          >
            <Page
              pageNumber={1}
              width={width}
              height={height}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        </div>
      )}
    </div>
  );
};

export default ThumbnailViewer;
